/**
 * The in-chat chat-storage warning (#758).
 *
 * ── Why it sits above the composer and does not auto-dismiss ────────────
 *
 * The member is about to attach something; the moment that matters is the
 * one just before they do. A toast would be gone by then. It stays until
 * they clear space or close it by hand.
 *
 * ── FOUR levels, shown once each ────────────────────────────────────────
 *
 * 80% is "you should know"; 95% is "the next file may be the one that
 * fails"; at or over the limit says so in the same words the refusal itself
 * uses; and a limit of 0 is its own sentence, because "delete some files"
 * cannot get anyone under zero.
 *
 * Closing one does not suppress the others — crossing a line is new
 * information, and a member who dismissed the amber banner last week has
 * not been told about the red one. Dismissal is remembered per LEVEL, in
 * memory only: it is a nudge, and persisting it would mean a member who
 * closed it once never sees it again however full they get.
 *
 * ── Silent when there is no limit to be near ────────────────────────────
 *
 * An unlimited member (`null`) — which includes every personal drive — has no
 * ceiling to warn about, and a percentage of nothing is not a number.
 *
 * A limit of ZERO is the opposite case and is NOT silent: see the `blocked`
 * level below.
 */
import { useState } from 'react'
import { useTranslation } from '@papan/i18n'

const WARN_AT = 0.8
const CRITICAL_AT = 0.95

/** Bytes as a short human string, binary units.
 *
 *  Matches `formatStorageBytes` (the refusal copy), NOT the storage page's
 *  `formatBytes`, which carries a second decimal BELOW 10 units: 1 GiB
 *  reads "1.00 GB" on the page and "1.0 GB" here. (An earlier version of
 *  this note illustrated it with "972.74 MB", which `formatBytes` never
 *  produces — over 10 units it rounds to one decimal too.) Matching the
 *  REFUSAL is the more useful of the two anyway: this banner is what a
 *  member reads just before one. */
function fmt(n: number): string {
  if (n < 1024) return `${n} B`
  const kb = n / 1024
  if (kb < 1024) return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} KB`
  const mb = kb / 1024
  if (mb < 1024) return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`
  const gb = mb / 1024
  if (gb < 1024) return `${gb < 10 ? gb.toFixed(1) : Math.round(gb)} GB`
  const tb = gb / 1024
  // NO PB ARM, and no argument for why one is unnecessary.
  //
  // I have now given four different reasons across rounds 35–38 — the
  // cap on `parseChatStorageLimit`, used bytes being under the limit, a
  // drive-size plausibility claim, and the cap chain again — and every
  // one was unsound while the conclusion happened to hold, which is
  // exactly why none of them got checked. The last was wrong because a
  // member can have NO limit in force (`limitBytes: null` is unlimited,
  // and every personal drive is), so nothing in that chain bounds bytes
  // accumulated before a limit was ever set.
  //
  // What is actually true: this formats one MEMBER's chat figures — both
  // mounts pass `chatQuota`, never a tenant quota or a role default —
  // and TB is the largest unit any deployment has needed. If one ever
  // reaches petabyte scale, this needs a PB arm and
  // `formatBytesForNotification` needs the same one.
  return `${tb < 10 ? tb.toFixed(1) : Math.round(tb)} TB`
}

export function ChatQuotaBanner({
  usedBytes, limitBytes, onManage, shape = 'docked',
}: {
  usedBytes: number
  /** `null` is unlimited; there is nothing to be near. */
  limitBytes: number | null
  onManage?: () => void
  /** `docked` sits flush on top of a bordered composer (ChatPanel) and so
   *  drops its own bottom edge. `standalone` is a separate block with its
   *  own margins, for the hero composer — which has no card chrome, so a
   *  banner welded to its top edge would have nothing to weld to. */
  shape?: 'docked' | 'standalone'
}) {
  const { t } = useTranslation('agent')
  const [dismissed, setDismissed] = useState<Record<string, boolean>>({})

  if (limitBytes === null) return null

  // REVIEW ROUND 1, FINDING 10 — a limit of 0 used to return null here, so
  // the ONE member who is definitely blocked saw nothing at all: an enabled
  // paperclip (that button keys on write permission, not on quota) and a 413
  // after they had already chosen the file. 0 is reachable two ways — an
  // admin typing it, which the parser accepts as a deliberate "cannot
  // upload", and any membership whose role is not in
  // DEFAULT_ROLE_CHAT_STORAGE, which resolves to 0 on purpose (fail closed).
  //
  // Its own level rather than the over-limit one: "delete some uploaded
  // files" is useless advice at a limit of 0, because deleting cannot get
  // them under it. Also keeps the ratio below from dividing by zero.
  const blocked = limitBytes <= 0
  const ratio = blocked ? 1 : usedBytes / limitBytes
  if (!blocked && ratio < WARN_AT) return null

  // FOUR levels. The first cut stopped at `critical` and told a
  // member who was already 40 MB OVER their limit "a couple more files and
  // uploads will be blocked" — a warning about a future that had already
  // happened, next to an upload notice saying it was blocked right now.
  // Over the line gets the SAME sentence the refusal itself uses.
  const level = blocked ? 'blocked' : ratio >= 1 ? 'over' : ratio >= CRITICAL_AT ? 'critical' : 'warn'
  if (dismissed[level]) return null
  const critical = level !== 'warn'

  return (
    <div
      className={`flex items-start gap-2.5 px-3.5 py-2.5 border text-[13px] ${
        shape === 'docked' ? 'rounded-t-xl border-b-0' : 'mx-4 mb-1.5 rounded-xl'
      } ${
        critical
          ? 'bg-rose-500/10 border-rose-500/30 text-rose-200'
          : 'bg-amber-500/10 border-amber-500/30 text-amber-200'
      }`}
    >
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true" className="shrink-0 mt-px">
        {critical
          ? <><circle cx="12" cy="12" r="9" /><path d="M12 7v6M12 16h.01" /></>
          : <><path d="M12 3l9.5 16.5h-19z" /><path d="M12 10v4M12 17h.01" /></>}
      </svg>
      {/* Text and action share a WRAPPING row. The action used to be
          `shrink-0` beside a `grow` sentence, which in a narrow chat panel
          left the sentence a column barely one word wide while the button
          kept its full size. Now the sentence asks for 10rem and the button
          drops to a second line when it cannot have it — a short banner two
          lines tall beats a tall one a word wide. `min-w-0` lets the sentence
          still shrink below 10rem when it is alone on its line. */}
      <div className="grow min-w-0 flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="grow basis-[10rem] min-w-0">
          {level === 'blocked'
            ? t('chatQuotaBlocked')
            : level === 'over'
            ? t('chatQuotaOver', { used: fmt(usedBytes), limit: fmt(limitBytes) })
            : level === 'critical'
              ? t('chatQuotaCritical', { left: fmt(limitBytes - usedBytes) })
              : t('chatQuotaWarn', { pct: Math.floor(ratio * 100), used: fmt(usedBytes), limit: fmt(limitBytes) })}
        </span>
        {onManage && (
          <button
            type="button"
            onClick={onManage}
            className={`shrink-0 text-xs px-3 py-1.5 rounded-lg font-semibold ${critical ? 'bg-rose-600 text-white' : 'border border-amber-500/40 themed-text'}`}
          >
            {t('chatQuotaManage')}
          </button>
        )}
      </div>
      {/* Stays pinned to the top-right corner however many lines the rest
          takes — a dismiss that slides down the banner is hard to aim at. */}
      <button
        type="button"
        aria-label={t('chatQuotaDismiss')}
        onClick={() => setDismissed((d) => ({ ...d, [level]: true }))}
        className="shrink-0 p-0.5 mt-px opacity-70 hover:opacity-100"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>
    </div>
  )
}
